import { Button, Tooltip, Dialog, Icon } from '@blueprintjs/core'
import React, { useState } from 'react'
import { formDataStore, subDataStore } from '../../../token-store'
import '../../user/modern-settings.css'

function formatDate(dateStr: string) {
  if (!dateStr || dateStr === '-') {
    return '-'
  }
  const date = new Date(dateStr)
  return isNaN(date.getTime())
    ? dateStr
    : date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
}

export const Subs = () => {
  const [isDialogOpen, setDialogOpen] = useState(false)
  const savedSubData = subDataStore.get() || {}
  const savedFormData = formDataStore.get() || {}
  const {
    subscription = '',
    createdAt = '-',
    till = '-',
    daysRemaining = '-',
    amount = '-',
    duration = '-',
    isCancelled = false,
    expired = false,
    refunds = [],
  } = savedSubData

  const days =
    typeof daysRemaining === 'string'
      ? parseInt(daysRemaining, 10)
      : daysRemaining

  // Calculate total refund amount
  const totalRefund = Array.isArray(refunds)
    ? refunds.reduce((sum, refund) => sum + (Number(refund) || 0), 0)
    : 0

  // Returns a status badge with a color according to the subscription state.
  const renderStatusBadge = () => {
    if (isCancelled) {
      return (
        <span style={{
          fontSize: '11px',
          fontWeight: 700,
          color: '#991b1b',
          backgroundColor: '#fee2e2',
          padding: '4px 12px',
          borderRadius: '12px',
          textTransform: 'uppercase',
          letterSpacing: '0.05em'
        }}>
          Cancelled
        </span>
      )
    } else if (days !== '-' && days < 5 && days >= 0) {
      return (
        <span style={{
          fontSize: '11px',
          fontWeight: 700,
          color: '#854d0e',
          backgroundColor: '#fef9c3',
          padding: '4px 12px',
          borderRadius: '12px',
          textTransform: 'uppercase',
          letterSpacing: '0.05em'
        }}>
          Expiring Soon
        </span>
      )
    } else if (expired) {
      return (
        <span style={{
          fontSize: '11px',
          fontWeight: 700,
          color: '#991b1b',
          backgroundColor: '#fee2e2',
          padding: '4px 12px',
          borderRadius: '12px',
          textTransform: 'uppercase',
          letterSpacing: '0.05em'
        }}>
          Expired
        </span>
      )
    } else {
      return (
        <span style={{
          fontSize: '11px',
          fontWeight: 700,
          color: '#065f46',
          backgroundColor: '#d1fae5',
          padding: '4px 12px',
          borderRadius: '12px',
          textTransform: 'uppercase',
          letterSpacing: '0.05em'
        }}>
          Active
        </span>
      )
    }
  }

  // A colored alert banner to catch the user's attention regarding the subscription status.
  const renderAlertBanner = (extraStyle: React.CSSProperties = {}) => {
    const baseStyle: React.CSSProperties = {
      padding: '10px 14px',
      borderRadius: '8px',
      fontSize: '12px',
      textAlign: 'center',
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxSizing: 'border-box',
      height: '100%',
      ...extraStyle
    }

    if ((isCancelled || expired) && days >= 0) {
      return (
        <div
          style={{
            ...baseStyle,
            background: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#991b1b',
          }}
        >
          Your subscription has been cancelled.
        </div>
      )
    } else if (days !== '-' && days < 5 && days >= 0) {
      return (
        <div
          style={{
            ...baseStyle,
            background: '#fffbf0',
            border: '1px solid #fef08a',
            color: '#854d0e',
          }}
        >
          {days === 0 ? 'Your subscription expires today.' : `Your subscription expires in ${days} day${(days > 1) ? 's' : ''}.`}
        </div>
      )
    } else if (days !== '-' && days < 0) {
      return (
        <div
          style={{
            ...baseStyle,
            background: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#991b1b',
          }}
        >
          Your subscription has expired.
        </div>
      )
    } else {
      return (
        <div
          style={{
            ...baseStyle,
            background: '#f0fdf4',
            border: '1px solid #bbf7d0',
            color: '#166534',
          }}
        >
          Your subscription is active.
        </div>
      )
    }
  }

  const renderChannelsList = () => {
    const isStarter = subscription.toLowerCase() === 'starter'
    const channels = ['Web Channel', 'Telegram', 'Slack', 'Facebook Messenger', 'WhatsApp']

    return (
      <div style={{ flex: '1 1 120px' }}>
        <div style={{ fontWeight: 600, color: '#475569', marginBottom: '8px', fontSize: '13px' }}>
          Supported Channels:
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {channels.map((ch, idx) => {
            const isWhatsApp = ch === 'WhatsApp'
            const isAvailable = !(isStarter && isWhatsApp)

            return (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', opacity: isAvailable ? 1 : 0.6 }}>
                <Icon
                  icon={isAvailable ? 'tick' : 'cross'}
                  iconSize={12}
                  color={isAvailable ? '#059669' : '#ef4444'}
                />
                <span style={{ color: isAvailable ? '#334155' : '#94a3b8', textDecoration: isAvailable ? 'none' : 'line-through' }}>
                  {ch}
                </span>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  const renderIncludesList = () => {
    const includes = ['LLM Support', 'HITL (Human in the Loop)', 'Bot Analytics']
    return (
      <div style={{ flex: '1 1 120px' }}>
        <div style={{ fontWeight: 600, color: '#475569', marginBottom: '8px', fontSize: '13px' }}>
          Includes:
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {includes.map((item, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
              <Icon icon='tick' iconSize={12} color='#059669' />
              <span style={{ color: '#334155' }}>{item}</span>
            </div>
          ))}
        </div>
      </div>
    )
  }

  const renderCustomizationsList = () => {
    const isStarter = subscription.toLowerCase() === 'starter'
    const itemStyle = { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontSize: '13px' }

    const starterFeatures = [
      'Chat window resize & color',
      'Input field styling',
      'Send button styling',
      'Webchat logo settings'
    ]

    const proFeatures = [
      'Chat window resize & color',
      'Chat bubble styling',
      'Input field styling',
      'Send button styling',
      'Webchat logo settings',
      'Ripple Effect',
      'Welcome Message Bubble',
      'xMati branding removal'
    ]

    const features = isStarter ? starterFeatures : proFeatures

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {features.map((feat, idx) => (
          <div key={idx} style={itemStyle}>
            <Icon icon='tick' iconSize={12} color='#059669' />
            <span style={{ color: '#334155' }}>{feat}</span>
          </div>
        ))}
      </div>
    )
  }

  const showNextSubs = !savedSubData.expired && savedFormData.nextSubs

  return (
    <div id='subscription_dropdown'>
      <Tooltip content='View Subscription' position='left'>
        <Button
          minimal
          onClick={() => setDialogOpen(true)}
          style={{
            fontSize: '13px',
            color: '#ffffff',
            fontWeight: 600,
            border: '1px solid rgba(255, 255, 255, 0.25)',
            borderRadius: '6px',
            padding: '5px 12px',
            background: 'rgba(255, 255, 255, 0.08)',
            transition: 'all 0.2s ease',
            textDecoration: 'none'
          }}
        >
          {subscription || 'No Active Plan'}
        </Button>
      </Tooltip>

      <Dialog
        isOpen={isDialogOpen}
        onClose={() => setDialogOpen(false)}
        title='Subscription Details'
        icon='credit-card'
        canOutsideClickClose
        canEscapeKeyClose
        className='modern-dialog'
        style={{
          width: '95%',
          maxWidth: 1050,
          height: 'auto',
          maxHeight: '95vh',
          display: 'flex',
          flexDirection: 'column',
          paddingBottom: 0,
        }}
      >
        <div className='modern-scrollbar' style={{ padding: '20px 24px', overflowY: 'auto', flex: 1, backgroundColor: '#f8fafc' }}>

          {/* Header Row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '14px', borderBottom: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <h2
                style={{
                  fontSize: '24px',
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #04a9e1, #028ec0)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  margin: 0,
                  letterSpacing: '-0.5px'
                }}
              >
                {subscription ? `${subscription} Plan` : 'No Active Plan'}
              </h2>
              {renderStatusBadge()}
            </div>

            <div
              style={{
                fontWeight: 700,
                fontSize: '11px',
                color: '#0369a1',
                backgroundColor: '#e0f2fe',
                padding: '4px 10px',
                borderRadius: '12px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              <Icon icon='cube' iconSize={11} color='#0369a1' />
              <span>
                {subscription.toLowerCase() === 'starter'
                  ? '3 Bots Included'
                  : '5 Bots Included'}
              </span>
            </div>
          </div>

          {/* 3 Columns Layout (Responsive with auto-fit) */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginBottom: '14px' }}>

            {/* Column 1: Supported Channels & Includes */}
            <div className='modern-card' style={{ padding: '16px', margin: 0, display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingBottom: '8px', borderBottom: '1px solid #f1f5f9' }}>
                <Icon icon='star' iconSize={14} color='#04a9e1' />
                <h4 style={{ margin: 0, fontSize: '12px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Channels & Includes
                </h4>
              </div>
              <div style={{ display: 'flex', gap: '16px', justifyContent: 'space-between', alignItems: 'stretch' }}>
                {renderChannelsList()}

                {/* Light Vertical Divider */}
                <div style={{ width: '1px', backgroundColor: '#e2e8f0', alignSelf: 'stretch' }} />

                {renderIncludesList()}
              </div>
            </div>

            {/* Column 2: Bot Customizations */}
            <div className='modern-card' style={{ padding: '16px', margin: 0, display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingBottom: '8px', borderBottom: '1px solid #f1f5f9' }}>
                <Icon icon='settings' iconSize={14} color='#04a9e1' />
                <h4 style={{ margin: 0, fontSize: '12px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Customizations
                </h4>
              </div>
              {renderCustomizationsList()}
            </div>

            {/* Column 3: Billing & Payments */}
            <div className='modern-card' style={{ padding: '16px', margin: 0, display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingBottom: '8px', borderBottom: '1px solid #f1f5f9' }}>
                <Icon icon='bank-account' iconSize={14} color='#04a9e1' />
                <h4 style={{ margin: 0, fontSize: '12px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Billing Details
                </h4>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {[
                  ['Start Date', formatDate(createdAt)],
                  ['End Date', formatDate(till)],
                  ['Total Amount', amount === '0' || amount === '-' ? '-' : `${amount}`],
                  ['Prev Refund', totalRefund > 0 ? `$${totalRefund.toFixed(2)}` : '-'],
                  ['Duration', duration === '30d' ? '30 Days' : duration === '5d' ? '5 Days' : duration === '7d' ? '7 Days' : duration],
                ].map(([label, value], idx, arr) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: '12px',
                      paddingBottom: idx < arr.length - 1 ? '8px' : 0,
                      borderBottom: idx < arr.length - 1 ? '1px solid #f1f5f9' : 'none'
                    }}
                  >
                    <span style={{ color: '#64748b', fontWeight: 500 }}>{label}</span>
                    <span style={{ color: '#0f172a', fontWeight: 600 }}>{value}</span>
                  </div>
                ))}
              </div>
            </div>

          </div>

          {/* Banners Row (Trial downgrade status and active subscription banners next to each other, taking 50% space each) */}
          {showNextSubs ? (
            <div style={{ display: 'flex', gap: '16px', marginTop: '16px', alignItems: 'stretch' }}>

              {/* Left Banner: Trial / Downgrade Notification */}
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  padding: '10px 14px',
                  borderLeft: '4px solid #0284c7',
                  background: '#f0f9ff',
                  borderRadius: '8px',
                  fontSize: '12px',
                  color: '#0369a1',
                  lineHeight: '1.6',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  wordBreak: 'break-word',
                  whiteSpace: 'normal'
                }}
              >
                <Icon icon='info-sign' iconSize={16} color='#0284c7' style={{ marginTop: '2px', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  {subscription === 'Trial' ? (
                    <span>
                      You opted for <strong>{savedFormData.nextSubs.plan}</strong> plan ({savedFormData.nextSubs.duration}) after Trial. You can update or change this selection at any time.
                    </span>
                  ) : (
                    <span>
                      Your plan will automatically be downgraded to <strong>{savedFormData.nextSubs.plan}</strong> ({savedFormData.nextSubs.duration}) on the expiry day.
                    </span>
                  )}
                </div>
              </div>

              {/* Right Banner: Alert Banner */}
              <div style={{ flex: 1, minWidth: 0 }}>
                {renderAlertBanner()}
              </div>

            </div>
          ) : (
            <div style={{ marginTop: '16px' }}>
              {renderAlertBanner()}
            </div>
          )}

        </div>
      </Dialog>
    </div>
  )
}
